import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getHot, searchTitles } from "@/lib/media";
import SectionHeading from "@/components/base/SectionHeading";
import TitleGrid from "@/components/base/TitleGrid";
import EmptyState from "@/components/base/EmptyState";

const HOT_KEYWORDS = ["科幻", "悬疑", "古装", "爱情", "2024", "美国"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);

  useEffect(() => {
    setKeyword((prev) => (prev !== q ? q : prev));
  }, [q]);

  useEffect(() => {
    if (keyword === q) return;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams();
      if (keyword.trim()) next.set("q", keyword.trim());
      setSearchParams(next);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [keyword, q, setSearchParams]);

  const results = q.trim() ? searchTitles(q) : [];
  const hot = getHot(6);

  usePageMeta({
    title: q ? `“${q}” 的搜索结果 - 西瓜影音` : "影视搜索 - 西瓜影音",
    description: q
      ? `西瓜影音为你找到与“${q}”相关的影视作品，共 ${results.length} 条结果，支持高清在线观看。`
      : "西瓜影音影视搜索，输入影片名称、演员、类型或地区，快速找到你想看的高清影视作品。",
    keywords: `影视搜索,${q || "电影,电视剧"},西瓜影音,高清在线观看`,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams();
    if (keyword.trim()) next.set("q", keyword.trim());
    setSearchParams(next);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10 animate-fade-in">
      <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-5" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-200">影视搜索</span>
      </nav>

      <header className="rounded-lg bg-gradient-to-r from-background-900 to-primary-950 border border-background-800 p-6 md:p-8 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4">
          影视搜索
        </h1>
        <form
          onSubmit={submit}
          className="flex items-center gap-2 bg-background-950/70 border border-background-700 rounded-full pl-5 pr-1.5 py-1.5 max-w-2xl focus-within:border-primary-500 transition-colors"
        >
          <i className="ri-search-line text-foreground-400"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="输入影片名称、演员、类型或地区"
            className="flex-1 bg-transparent text-sm md:text-base text-foreground-100 placeholder:text-foreground-500 outline-none"
            aria-label="搜索影视"
          />
          <button
            type="submit"
            className="px-5 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            搜索
          </button>
        </form>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-xs text-foreground-500">热门搜索：</span>
          {HOT_KEYWORDS.map((kw) => (
            <button
              key={kw}
              type="button"
              onClick={() => {
                setKeyword(kw);
                const next = new URLSearchParams();
                next.set("q", kw);
                setSearchParams(next);
              }}
              className="px-3 py-1 rounded-full bg-background-800 text-foreground-300 hover:bg-background-700 hover:text-foreground-100 text-xs whitespace-nowrap cursor-pointer transition-colors"
            >
              {kw}
            </button>
          ))}
        </div>
      </header>

      {q.trim() ? (
        results.length > 0 ? (
          <>
            <p className="text-sm text-foreground-400 mb-5">
              关键词 <span className="text-primary-400 font-medium">“{q}”</span> 共找到{" "}
              <span className="text-foreground-100 font-semibold">{results.length}</span> 条结果
            </p>
            <TitleGrid items={results} highlight={q} />
          </>
        ) : (
          <div className="space-y-8">
            <EmptyState
              icon="ri-emotion-sad-line"
              title={`没有找到与“${q}”相关的影视作品`}
              description="换个关键词试试吧，或者看看下面这些大家都在看的热门内容。"
            />
            <div>
              <SectionHeading title="热门影视推荐" subtitle="大家正在看的高分作品" />
              <TitleGrid items={hot} />
            </div>
          </div>
        )
      ) : (
        <div className="space-y-8">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词，开始你的观影之旅"
            description="支持按影片名称、演员、类型或地区搜索，比如「科幻」「悬疑」「2024」。"
          />
          <div>
            <SectionHeading title="热门影视推荐" subtitle="不知道看什么？先从热门开始" />
            <TitleGrid items={hot} />
          </div>
        </div>
      )}
    </div>
  );
}